import React, { useEffect, useRef, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import { dividiAvvisi, testoDellAvviso, AVVISI_IN_RIGA } from './avvisiTestata';

/* ------------------------------------------------------------------
 * SegniAvvisi — gli avvisi della testata della cartella, stile nuovo,
 * dopo le allergie: i «sì» a patologie cutanee ed herpes (Alex, 3/11/2026,
 * migration 356), la lettura non riuscita delle risposte, e gli avvisi delle
 * schede cliniche.
 *
 * PERCHE' UN COMPONENTE A SE' (NO del beta sul ramo patologie-herpes,
 * 3/11/2026, scenario 2603): con l'Herpes accanto all'allergia la testa a
 * 1024x768 era arrivata a 286 px, oltre un terzo della finestra (256), e
 * smetteva di restare ferma. Ogni avviso nuovo era un segno largo quanto il
 * suo testo, e i segni andavano a capo. Le regole, qui in un posto solo:
 *   - ogni avviso sta in una riga sola: il titolo resta intero, il testo si
 *     tronca coi puntini (`rm-tronca`) e sta intero nel `title`;
 *   - in riga ne stanno AVVISI_IN_RIGA; gli altri in un segno compatto «+N»,
 *     un pulsante col suo aria-label, che apre l'elenco sopra la pagina
 *     (in assoluto: aprirlo non allunga la testa);
 *   - le ALLERGIE non passano di qui: restano scritte tutte, nel loro segno
 *     (e' l'informazione piu' importante della scheda).
 * Quanto si allarga ognuno lo decide SchedaPazienteConsole.css
 * (.crt-segno--stretto). La misura vera e' quella dello scenario 2603.
 * ------------------------------------------------------------------ */

/* Una voce sola per i tre tipi di avviso, nell'ordine dello schermo. */
export function vociDegliAvvisi({ risposte = [], risposteNonLette = false, avvisiSchede = [] } = {}) {
    return [
        ...(risposte || []).map((a) => ({ chiave: a.chiave, segno: a.chiave, titolo: a.titolo, testo: a.testo, porta: true })),
        ...(risposteNonLette ? [{ chiave: 'risposte-non-lette', segno: 'risposte', lettura: 'non-riuscita', tono: 'attenzione',
            titolo: 'Patologie cutanee ed herpes', testo: 'non riesco a leggerle, apri l\'anamnesi', porta: true }] : []),
        ...(avvisiSchede || []).map((a) => ({ chiave: `scheda-${a.campo_id}`, segno: 'avviso-scheda', titolo: 'Attenzione',
            testo: a.allarme_testo, scheda: a.scheda, porta: false })),
    ];
}

/* Il testo intero, per il `title`: quello che i puntini tolgono. */
const intero = (v) => `${testoDellAvviso(v)}${v.scheda ? ` (${v.scheda})` : ''}`;

const Segno = ({ v, onApri, pieno = false }) => {
    const classi = ['crt-segno', v.tono === 'attenzione' ? 'crt-segno--attenzione' : 'crt-segno--errore'];
    if (!pieno) classi.push('crt-segno--stretto');
    const corpo = (
        <>
            <TriangleAlert size={14} strokeWidth={2} aria-hidden="true" />
            {pieno || !v.testo ? (
                <>
                    <span className="crt-segno-titolo">{v.testo ? `${v.titolo}:` : v.titolo}</span>
                    {v.testo ? ' ' : null}
                    {v.testo && (
                        <span className="crt-segno-testo">
                            {v.testo}
                            {v.scheda && <>{' '}<em className="sp-allergia-gravita">{v.scheda}</em></>}
                        </span>
                    )}
                </>
            ) : (
                /* NEL SEGNO STRETTO titolo e testo sono UNA riga sola, che si
                   tronca intera coi puntini (NO del beta su d292a74b, 4/11/2026:
                   col titolo e il testo in due span che si troncano tutti e due,
                   il testo del primo, tagliato, passava sotto il secondo, e la
                   rifinitura li dava sovrapposti). Testo semplice, senza
                   elementi dentro, cosi' quello che il taglio nasconde e' di chi
                   porta rm-tronca e il title. */
                <span className="crt-segno-testo rm-tronca" title={intero(v)}>
                    {`${v.titolo}: ${v.testo}`}
                    {v.scheda && <>{' '}<em className="sp-allergia-gravita">{v.scheda}</em></>}
                </span>
            )}
        </>
    );
    return v.porta ? (
        <button type="button" data-segno={v.segno} data-lettura={v.lettura} className={classi.join(' ')}
                title={intero(v)} onClick={onApri}>
            {corpo}
        </button>
    ) : (
        <span data-segno={v.segno} className={classi.join(' ')} title={intero(v)}>{corpo}</span>
    );
};

const SegniAvvisi = ({ risposte, risposteNonLette, avvisiSchede, onApri, quanti = AVVISI_IN_RIGA }) => {
    const [aperto, setAperto] = useState(false);
    const altriRef = useRef(null);
    const { inRiga, altri } = dividiAvvisi(vociDegliAvvisi({ risposte, risposteNonLette, avvisiSchede }), quanti);

    /* L'elenco aperto si chiude con Esc e con un clic fuori. */
    useEffect(() => {
        if (!aperto) return undefined;
        const fuori = (e) => { if (altriRef.current && !altriRef.current.contains(e.target)) setAperto(false); };
        const esc = (e) => { if (e.key === 'Escape') setAperto(false); };
        document.addEventListener('mousedown', fuori);
        document.addEventListener('keydown', esc);
        return () => { document.removeEventListener('mousedown', fuori); document.removeEventListener('keydown', esc); };
    }, [aperto]);
    useEffect(() => { if (!altri.length && aperto) setAperto(false); }, [altri.length, aperto]);

    return (
        <>
            {inRiga.map((v) => <Segno key={v.chiave} v={v} onApri={onApri} />)}
            {altri.length > 0 && (
                <span className="crt-segni-altri" ref={altriRef}>
                    <button type="button" data-segno="altri"
                            className="crt-segno crt-segno--errore crt-segno--altri"
                            aria-label={`Altri ${altri.length} avvisi: apri l'elenco`}
                            aria-expanded={aperto} aria-controls="crt-segni-elenco"
                            title={altri.map(intero).join('\n')}
                            onClick={() => setAperto((x) => !x)}>
                        +{altri.length}
                    </button>
                    {aperto && (
                        <ul id="crt-segni-elenco" className="crt-segni-elenco" data-segni-altri="">
                            {altri.map((v) => (
                                <li key={v.chiave}>
                                    <Segno v={v} pieno onApri={() => { setAperto(false); if (onApri) onApri(); }} />
                                </li>
                            ))}
                        </ul>
                    )}
                </span>
            )}
        </>
    );
};

export default SegniAvvisi;
